import React from 'react';
import { toast } from 'react-hot-toast';
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import { genericErrorText, statusText, networkErrorText } from '../i18n/serverErrors';

/**
 * THE one notification channel. Every toast in the app - our own calls, antd's static
 * `message.*` / `notification.*` (re-routed by installNotifyBridge in main.tsx) and the axios
 * interceptor - lands in the single top-centre <Toaster> in App.tsx, so a failure never shows as
 * two popups in two corners.
 *
 *   notify.success(t('x:saved'));
 *   notify.error(t('x:saveFailed'), { description: err.message });
 *
 * - Identical text while still visible updates the one toast instead of stacking another.
 * - Right after the interceptor has reported a server or network error, a caller's own
 *   follow-up error ("Failed to load X") is dropped: the user has already been told.
 * - Technical text (exception names, stack lines, "Request failed with status code 500",
 *   provider/config details) is never shown; a plain message takes its place.
 */
export type NotifyKind = 'success' | 'error' | 'info' | 'warning' | 'loading';

export interface NotifyOptions {
  /** Same id = the same toast (replaced in place). Defaults to kind + text. */
  id?: string;
  description?: React.ReactNode;
  /** Milliseconds; 0 keeps it until dismissed. */
  duration?: number;
}

const DEFAULT_MS: Record<NotifyKind, number> = { success: 3000, info: 3500, warning: 4500, error: 4500, loading: Infinity };

/** Text a person cannot act on: implementation detail that leaked from a server or library. */
const TECHNICAL = [
  /exception\b/i,
  /\bat [\w$.<>]+\(/,                     // stack frame
  /object reference not set/i,
  /cannot read propert/i,
  /is not a function/i,
  /\[object object\]/i,
  /^(undefined|null|nan)$/i,
  /\bsql\b|sqlstate|constraint ['"]?\w+['"]? |duplicate key/i,
  /api[\s_-]?key|secret|token is invalid|invalid_grant|unauthorized_client/i,
  /econn(refused|reset|aborted)|enotfound|socket hang up/i,
  /<\s*(html|!doctype|body)\b/i,          // gateway HTML page
  /\bfiles? (is |are )?not (correct|valid)\b/i,
  /\b(stack ?trace|traceid|correlation[- ]?id)\b/i,
];

export const isTechnicalMessage = (text: string | null | undefined): boolean =>
  !!text && TECHNICAL.some((re) => re.test(text));

/** Axios' own wording, mapped to ours. */
const libraryText = (text: string): string | null => {
  const m = /^request failed with status code (\d{3})$/i.exec(text.trim());
  if (m) { const s = Number(m[1]); return statusText(s) ?? genericErrorText(s); }
  if (/^network error$/i.test(text.trim())) return networkErrorText('unreachable').title;
  if (/^timeout of \d+ms exceeded$/i.test(text.trim())) return networkErrorText('timeout').title;
  return null;
};

/** What an error toast may say: the text itself when it is meant for people, ours otherwise. */
export const userFacingError = (text: string): string =>
  libraryText(text) ?? (isTechnicalMessage(text) ? genericErrorText(0) : text);

let serverErrorAt = 0;
const SERVER_ERROR_QUIET_MS = 1500;
/** The interceptor calls this when it has reported a request failure itself. */
export const markServerErrorShown = (): void => { serverErrorAt = Date.now(); };

const ICONS: Partial<Record<NotifyKind, React.ReactElement>> = {
  success: <CheckCircleFilled style={{ color: 'var(--success-ink)' }} />,
  error: <CloseCircleFilled style={{ color: 'var(--danger-ink)' }} />,
  warning: <ExclamationCircleFilled style={{ color: 'var(--warn-ink)' }} />,
  info: <InfoCircleFilled style={{ color: 'var(--primary)' }} />,
};

const plain = (node: React.ReactNode): string =>
  typeof node === 'string' || typeof node === 'number' ? String(node) : '';

export const show = (kind: NotifyKind, content: React.ReactNode, opts: NotifyOptions = {}): string => {
  let title = content;
  let description = opts.description;
  if (kind === 'error') {
    if (!opts.id && Date.now() - serverErrorAt < SERVER_ERROR_QUIET_MS) return '';
    if (typeof title === 'string') title = userFacingError(title);
    if (typeof description === 'string') description = isTechnicalMessage(description) || libraryText(description) ? undefined : description;
  }
  if (title == null || title === '') return '';
  if (plain(description) && plain(description) === plain(title)) description = undefined;

  const body: React.ReactElement | string = description
    ? <div className="notify"><div className="notify__title">{title}</div><div className="notify__desc">{description}</div></div>
    : typeof title === 'string' || typeof title === 'number' ? String(title) : <>{title}</>;
  const id = opts.id ?? `${kind}:${plain(title) || Math.random()}`;
  const duration = opts.duration === 0 ? Infinity : opts.duration ?? DEFAULT_MS[kind] + (description ? 1500 : 0);

  if (kind === 'loading') return toast.loading(body, { id, duration });
  return toast(body, { id, duration, icon: ICONS[kind], ariaProps: { role: kind === 'error' ? 'alert' : 'status', 'aria-live': kind === 'error' ? 'assertive' : 'polite' } });
};

export const notify = {
  success: (c: React.ReactNode, o?: NotifyOptions) => show('success', c, o),
  error: (c: React.ReactNode, o?: NotifyOptions) => show('error', c, o),
  info: (c: React.ReactNode, o?: NotifyOptions) => show('info', c, o),
  warning: (c: React.ReactNode, o?: NotifyOptions) => show('warning', c, o),
  loading: (c: React.ReactNode, o?: NotifyOptions) => show('loading', c, o),
  dismiss: (id?: string) => toast.dismiss(id),
};

export default notify;
